import React from 'react';
import {
  Box,
  Button,
  FlexListItemStatus,
  Inscription,
  NoUsersIcon,
  Preloader,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import Permissions from '@/common/permissions';
import { DropButton } from '@/components/controls/DropButton';
import { IAuthorizedUserDtoStatusEnum } from '@/dtos';
import { useHasAccess } from '@/modules/core';

import { AuthorizedUser } from '../../authorizedUsers.types';

import useAuthorizedUsersList, {
  ACTION_COLUMN_FLEX,
  COLUMNS,
} from './hooks/useAuthorizedUsersList';

interface StyledButtonProps {
  $isWarning?: boolean;
}

const StyledNameText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 20px;
`;

const StyledActionButton = styled(Button)<StyledButtonProps>`
  height: ${({ theme }) => theme.spacings.spacing20} !important;
  min-height: ${({ theme }) => theme.spacings.spacing20} !important;
  ${({ theme }) => theme.fonts.text14Regular};
  font-weight: 400 !important;
  margin-bottom: ${({ theme }) => theme.spacings.spacing16};
  color: ${({ theme, $isWarning }) => ($isWarning ? `${theme.colors.danger} !important` : '')};
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    color: ${({ theme, $isWarning }) =>
      $isWarning ? theme.colors.danger : theme.colors.textAccent};
  }
`;

const StyledStatusWrapper = styled(Box)`
  .status-text {
    margin-left: ${({ theme }) => theme.spacings.spacing8} !important;
  }
`;


interface Props {
  authorizedUsers: AuthorizedUser[];
  isLoading: boolean;
  onEdit: (user: AuthorizedUser) => void;
  onDeactivate: (user: AuthorizedUser) => void;
  onReactivate: (user: AuthorizedUser) => void;
}

const AuthorizedUsersList: React.FC<Props> = ({
  authorizedUsers,
  isLoading,
  onEdit,
  onDeactivate,
  onReactivate,
}) => {
  const hasEditPermission = useHasAccess([{ permission: Permissions.EDIT_AUTHORIZED_USERS }]);
  const { formattedUsers } = useAuthorizedUsersList({
    authorizedUsers,
    onEdit,
    onDeactivate,
    onReactivate,
  });

  if (isLoading) {
    return (
      <Box pad={{ top: 'spacing24' }}>
        <Preloader testId="authorized-users-list_preloader" />
      </Box>
    );
  }

  if (!authorizedUsers.length) {
    return (
      <Box
        align="center"
        justify="center"
        pad={{ vertical: 'spacing32' }}
        data-testid="authorized-users_empty-state"
      >
        <Box margin={{ bottom: 'spacing12' }}>
          <NoUsersIcon />
        </Box>
        <Inscription size="medium" weight="bold" color="textBody" data-testid="authorized-users_empty-state_text">
          There are no authorized users added yet
        </Inscription>
      </Box>
    );
  }

  const allColumns = hasEditPermission
    ? [...COLUMNS, { key: 'actions', title: 'Actions', flex: ACTION_COLUMN_FLEX }]
    : COLUMNS;

  return (
    <Box
      margin={{ top: 'spacing12' }}
      data-testid="authorized-users-list"
    >
      <Box
        direction="row"
        align="center"
        background="listHeaderBackgroundColor"
        round="container2Round"
        pad={{ vertical: 'spacing8', horizontal: 'spacing24' }}
      >
        {allColumns.map((col) => (
          <Box key={col.key} style={{ flex: col.flex }} data-testid={`authorized-users-list_header_${col.key}`}>
            <Text size="12px" weight="bold" color="textBody">
              {col.title}
            </Text>
          </Box>
        ))}
      </Box>

      {formattedUsers.map((user) => (
        <Box
          key={user.id}
          direction="row"
          align="center"
          background="canvas"
          round="container2Round"
          border={{ color: 'border2', size: '1px' }}
          pad={{ vertical: 'spacing18', horizontal: 'spacing24' }}
          margin={{ top: 'spacing8' }}
          data-testid={`authorized-users-list_item_${user.id}`}
        >
          <Box style={{ flex: '1', overflow: 'hidden' }}>
            <StyledNameText
              size="14px"
              weight="bold"
              color={user.isActive ? 'textBody' : 'textDisabled'}
              data-testid={`authorized-users-list_item_${user.id}_first-name`}
            >
              {user.firstName}
            </StyledNameText>
          </Box>
          <Box style={{ flex: '1', overflow: 'hidden' }}>
            <StyledNameText
              size="14px"
              weight="bold"
              color={user.isActive ? 'textBody' : 'textDisabled'}
              data-testid={`authorized-users-list_item_${user.id}_last-name`}
            >
              {user.lastName}
            </StyledNameText>
          </Box>
          <Box style={{ flex: '1' }}>
            <Text
              size="medium"
              color={user.isActive ? undefined : 'textDisabled'}
              data-testid={`authorized-users-list_item_${user.id}_relationship`}
            >
              {user.relationship}
            </Text>
          </Box>
          <Box style={{ flex: '1' }}>
            <Text
              size="medium"
              color={user.isActive ? undefined : 'textDisabled'}
              data-testid={`authorized-users-list_item_${user.id}_date-of-birth`}
            >
              {user.dateOfBirth}
            </Text>
          </Box>
          <Box style={{ flex: '1' }}>
            <Text
              size="medium"
              color={user.isActive ? undefined : 'textDisabled'}
              data-testid={`authorized-users-list_item_${user.id}_date-added`}
            >
              {user.dateAdded}
            </Text>
          </Box>
          <Box style={{ flex: '1' }}>
            <StyledStatusWrapper>
              <FlexListItemStatus
                status={user.status === IAuthorizedUserDtoStatusEnum.ACTIVE ? 'Active' : 'Inactive'}
                testId={`authorized-users-list_item_${user.id}_status`}
              />
            </StyledStatusWrapper>
          </Box>
          {hasEditPermission && (
            <Box
              style={{ flex: ACTION_COLUMN_FLEX }}
              align="end"
              data-testid={`authorized-users-list_item_${user.id}_actions`}
            >
              <DropButton testId={`authorized-users-list_item_${user.id}`}>
                {user.isActive ? (
                  <>
                    <StyledActionButton
                      type="button"
                      data-testid={`authorized-users-list_item_${user.id}_edit`}
                      onClick={user.onEdit}
                    >
                      Edit
                    </StyledActionButton>
                    <StyledActionButton
                      type="button"
                      $isWarning
                      data-testid={`authorized-users-list_item_${user.id}_deactivate`}
                      onClick={user.onDeactivate}
                    >
                      Deactivate
                    </StyledActionButton>
                  </>
                ) : (
                  <>
                    <StyledActionButton
                      type="button"
                      data-testid={`authorized-users-list_item_${user.id}_edit`}
                      onClick={user.onEdit}
                    >
                      Edit
                    </StyledActionButton>
                    <StyledActionButton
                      type="button"
                      data-testid={`authorized-users-list_item_${user.id}_reactivate`}
                      onClick={user.onReactivate}
                    >
                      Activate
                    </StyledActionButton>
                  </>
                )}
              </DropButton>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default AuthorizedUsersList;
